import { Box, Flex, Input, Button, FormControl, FormLabel, useToast, Tooltip, Textarea } from '@chakra-ui/react';
import emailjs from '@emailjs/browser';
import { useRef, useState, useEffect } from 'react';

/**
 * The Contact section
 * @returns The Contact Component
 */
export default function Contact() {
	const form = useRef<HTMLFormElement | null>(null);
	const toast = useToast();

	// States for input validation
	const [ isName, setIsName ] = useState(true);
	const [ isEmail, setIsEmail ] = useState(true);
	const [ isSending, setIsSending ] = useState(false);

	// State for form inputs
	const [formData, setFormData] = useState({
		user_name: '',
		user_email: '',
		message: ''
	});
	const [isFormValid, setIsFormValid] = useState(false);

	// Function to update form data
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, type: string) => {

		// Get input value
		const { value } = e.target;

		// Set form data based on input type
		if (type === 'name') {

			// Make the first letter of each word uppercase
			const name = value.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

			// No invalid characters
			if (value === '' || value.match(/^[a-zA-Z\s]*$/)) {
				setIsName(true);
			} else {
				setIsName(false);
			}

			// Update name data
			setFormData({
				...formData,
				user_name: name
			});

		} else if (type === 'email') {

			// Make sure email ends in .com, .org, .net, .edu, .gov. If it doesnt, set isEmail to false
			if (value === '' || value.match(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)) {
				setIsEmail(true);
			} else {
				setIsEmail(false);
			}
			
			// Update email data
			setFormData({
				...formData,
				user_email: value
			});

		} else {
			
			// Update message data
			setFormData({
				...formData,
				message: value
			});

		}
	};

	// Validate form
	useEffect(() => {
		const { user_name, user_email, message } = formData;
		setIsFormValid(user_name.trim() !== '' && user_email.trim() !== '' && message.trim() !== '' && isName && isEmail);
	}, [formData, isName, isEmail]);

	const sendEmail = (e: { preventDefault: () => void; }) => {
		e.preventDefault();

		// Set sending to true
		setIsSending(true);

		emailjs
			.sendForm(
				'service_76x9j42',
				'template_3ac2opq',
				form.current!,
				{
					publicKey: 'HN9O4BdkCSBA0G7Vo',
				})
			.then(() => {
				toast({
					title: 'Email Sent!',
					description: 'I will get back to you as soon as possible.',
					status: 'success',
					duration: 9000,
					isClosable: true,
				});
				// Reset form
				setFormData({
					user_name: '',
					user_email: '',
					message: ''
				});

				// Set sending to false
				setIsSending(false);
			},
			(error) => {
				console.log('FAILED...', error.text);
			}
		);
	};

	return (
		<Flex
			id="contact"
			flexDirection={"column"}
			alignItems={"center"}
			justifyContent={"center"}
			minW={"100%"}
			maxW={"100%"}
		>
			{/* Title */}
			<Box
				fontSize={"4xl"}
				fontFamily={"Source Code Pro Variable"}
				fontWeight={"600"}
                width={"100%"}
                textAlign={"center"}
				color={"white.200"}
				padding={"5%"}
			>
				Let's Get in Contact!
			</Box>

			{/* Subheader */}
			<Box
				width={"80%"}
				fontSize={{ base: "lg", md: "xl" }}
				fontFamily={"Source Code Pro Variable"}
				fontWeight={"500"}
				textAlign={{ base: "left", md: "left" }}
				color={"white.300"}
				padding={"15px"}
			>
				I'm always open to new opportunities and collaborations! Send me a message and I'll get back to you as soon as possible.
			</Box>
			
			{/* Contact Form */}
			<FormControl
				flexDirection={"column"}
				width={"80%"}
				alignItems={"center"}
				padding={"15px"}
			>
				<form ref={form} onSubmit={sendEmail}>
					{/* Name */}
					<FormLabel
						fontFamily={"Source Code Pro Variable"}
						fontWeight={"500"}
						fontSize={"xl"}
						color={"white.200"}
					>
						Name
					</FormLabel>
					<Tooltip
						label="Please enter a valid name."
						color='white'
						backgroundColor={"red"}
						variant={"solid"}
						isOpen={!isName}
						placement='top'
					>
						<Input
							type="text"
							color={"white.200"}
							name="user_name"
							placeholder="Name"
							size="md"
							borderRadius={0}
							marginBottom={5}
							isInvalid={!isName}
							value={formData.user_name}
							onChange={(e) => handleInputChange(e, 'name')}
						/>
					</Tooltip>
					
					{/* Email */}
					<FormLabel
						fontFamily={"Source Code Pro Variable"}
						fontWeight={"500"}
						fontSize={"xl"}
						color={"white.200"}
					>
						Email
					</FormLabel>
					<Tooltip
						label="Please enter a valid email."
						color='white'
						backgroundColor={"red"}
						variant={"solid"}
						isOpen={!isEmail}
						placement='top'
					>
						<Input
							type="email"
							color={"white.200"}
							name="user_email"
							placeholder="Email"
							size="md"
							borderRadius={0}
							marginBottom={5}
							isInvalid={!isEmail}
							value={formData.user_email}
							onChange={(e) => handleInputChange(e, 'email')}
						/>
					</Tooltip>
					
					{/* Message */}
					<FormLabel
						fontFamily={"Source Code Pro Variable"}
						fontWeight={"500"}
						fontSize={"xl"}
						color={"white.200"}
					>
						Message
					</FormLabel>
					<Textarea
						color={"white.200"}
						name="message"
						placeholder="Message"
						borderRadius={0}
						marginBottom={"30px"}
						value={formData.message}
						onChange={(e) => handleInputChange(e, 'message')}
					/>
					
					{/* Submit */}
					<Button
						type="submit"
						fontFamily={"Source Code Pro Variable"}
						fontWeight={"600"}
						fontSize={"2xl"}
						color={"black.200"}
						backgroundColor={"blue.300"}
						borderRadius={0}
						marginBottom={5}
						isDisabled={!isFormValid}
						width={"100%"}
						height={"80px"}
						isLoading={isSending}
					>
						Send Message
					</Button>
				</form>
			
			</FormControl>
			
		</Flex>
	);
}
